import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Link, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLandingRouter from '../AppLandingRouter';
import { useAuth } from '../../hooks/useAuth';

interface LoginFormProps {
  onBackClick: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onBackClick }) => {
  const { login, logout, isAuthenticated, loading, error } = useAuth();
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const success = await login({ email, password });
    if (success) {
      setLoggedIn(true);
    }
  };

  const handleLogout = () => {
    logout();
    setLoggedIn(false);
  };

  if (loggedIn) {
    return <AppLandingRouter onLogout={handleLogout} />;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {/* Spinner Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255,255,255,0.95)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
            Hang Tight!
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, textAlign: 'center' }}>
            Logging you in... Checking your credentials
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={180} thickness={3} sx={{ color: '#00A3FF' }} />
          </Box>
        </Box>
      )}

      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBackClick}
        sx={{
          mb: 4,
          color: 'text.secondary',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main',
          }
        }}
      >
        Back
      </Button>

      {/* Title */}
      <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
        Welcome back!
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email Field */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email address"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Password Field */}
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type="password"
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        sx={{
          mt: 2,
          mb: 2,
          py: 1.5,
          fontSize: '1.1rem',
          borderRadius: 8,
          backgroundColor: '#00A3FF',
          '&:hover': {
            backgroundColor: '#0091E6'
          }
        }}
      >
        Log in
      </Button>

      {/* Forgot Password Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          href="#"
          sx={{
            color: '#00A3FF',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Forgot password?
        </Link>
      </Box>
    </Box>
  );
};

export default LoginForm;
